import {
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { Attribute } from '@core/http-types';
import { TranslatePipe } from '@core/i18n-legacy';
import type { PaginationState } from '@core/ngrx-legacy';
import { AttrDataScope, SourcesActions, sourcesSelectors } from '@core/tags-and-sources-data';
import type { AttributesLoadMore } from '@core/user-interface-legacy';
import {
  AttributesComponent,
  DropdownHeaderComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
} from '@core/user-interface-legacy';
import type { Editable } from '@core/utils-legacy';

@Component({
  selector: 'rt-candidate-sources-typeahead-wrapper',
  templateUrl: './candidate-sources-typeahead-wrapper.component.html',
  styleUrls: ['./candidate-sources-typeahead-wrapper.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    HeadingComponent,
    AttributesComponent,
    DropdownHeaderComponent,
    IconButtonComponent,
    RouterLink,
    TranslatePipe,
    IconComponent,
  ],
})
export class CandidateSourcesTypeaheadWrapperComponent {
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly displayLimit = 20;

  public readonly withHeading = input<boolean>(true);
  public readonly withEmptyState = input<boolean>(false);
  public readonly hasNoMargin = input<boolean>(false);

  public readonly sources = input<
    Editable<
      Array<{
        value: string;
        processing?: boolean;
      }>
    >
  >(undefined!);

  public readonly limitItems = input<boolean>(false);

  public deleteSource = output<Attribute>();
  public saveSource = output<Attribute>();

  public sourcesTypeahead: {
    isLoading: boolean;
    meta: PaginationState | null;
    rows: Attribute[];
  } = {
    isLoading: false,
    meta: null,
    rows: [],
  };
  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    const isPending$ = this.store.select(
      sourcesSelectors.isPending({
        scope: AttrDataScope.Typeahead,
      }),
    );
    const paginatedAttributes$ = this.store.select(
      sourcesSelectors.paginatedAttributeSelector({
        scope: AttrDataScope.Typeahead,
      }),
    );

    combineLatest([paginatedAttributes$, isPending$])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(([paginatedAttributes, isPending]) => {
        this.sourcesTypeahead = {
          ...paginatedAttributes,
          rows: paginatedAttributes.rows.filter(
            ({ value }) =>
              !this.sources()
                .value?.map(source => source.value)
                .includes(value),
          ),
          isLoading: isPending,
        };
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public sourceTypeaheadUpdate(query: string): void {
    const params = {
      query,
      page: 1,
      limit: 100,
    };

    this.store.dispatch(
      SourcesActions.fetchPaginatedCollection({
        payload: {
          params,
          scope: AttrDataScope.Typeahead,
        },
      }),
    );
  }

  public loadMoreSources(params: AttributesLoadMore): void {
    this.store.dispatch(
      SourcesActions.fetchPaginatedCollection({
        payload: { params, scope: AttrDataScope.Typeahead, add: true },
      }),
    );
  }
}
